import { LitElement, html, css, unsafeCSS, render } from 'lit';
import styles from './tag-list.scss?inline';
import { map } from 'lit/directives/map.js';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { hideAndGetOverflowItems, resetVisibility } from '@/helpers/moveHiddenToDropdown';

export default class SotbitTagList extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        open: { type: Boolean },
        hiddenCount: { type: Number, state: true },
        connected: { type: Boolean, reflect: true },
    };

    constructor() {
        super();

        this._dropdownItems = [];
        this.hiddenCount = 0;
        this.connected = false;
    }

    connectedCallback() {
        super.connectedCallback();
        this._setupResizeObserver();
        requestAnimationFrame(() => {
            this._checkNeedDropdown();
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._cleanupResizeObserver();
    }

    _setupResizeObserver() {
        this._cleanupResizeObserver();

        this._resizeObserver = new ResizeObserver(this._checkNeedDropdown);
        this._resizeObserver.observe(this);
    }

    _cleanupResizeObserver() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }

    _show = debounce(() => {
        if (this.connected) {
            return;
        }

        this._checkNeedDropdown();
        this.connected = true;
    }, 60);

    _checkNeedDropdown = throttle(() => {
        this._resetVisibility();
        const dropdownItems = hideAndGetOverflowItems(this, { cloneItems: true });
        this._dropdownItems = dropdownItems?.filter((item) => !item.hasAttribute('hidden')) || [];
        this.hiddenCount = this._dropdownItems?.length || 0;

        this.toggleAttribute('show-dropdown', this.hiddenCount > 0);
        this._renderDropdownItems();

        this._show();
    }, 60);

    _resetVisibility() {
        this._dropdownItems = [];

        this._clearDropdown();
        resetVisibility(this);
    }

    _handleDropdownSlotChange(e) {
        const slots = e.target.assignedElements();
        this._dropdownNode = slots[0];
        this._clearDropdown();
        this._renderDropdownItems();
    }

    _clearDropdown() {
        if (!this._dropdownNode) {
            return;
        }

        for (const child of this._dropdownNode.children) {
            if (child?.getAttribute('slot') !== 'anchor') {
                child?.remove();
            }
        }
    }

    _renderDropdownItems() {
        if (!this._dropdownNode) {
            return;
        }

        render(
            html`
                ${map(this._dropdownItems, (item) => {
                    item.removeAttribute('hidden');

                    return html`${item}`;
                })}
            `,
            this._dropdownNode
        );
    }

    render() {
        const showDropdown = this.hiddenCount > 0;

        return html`
            <slot></slot>
            ${showDropdown ? html` <slot name="dropdown" @slotchange="${this._handleDropdownSlotChange}"></slot> ` : ''}
        `;
    }
}
